'use client';

import React from 'react';
import Image from 'next/image';

// ─── OFFICIAL CHARACTER ASSET PATHS ──────────────────────────────────────────
// These are the ONLY canonical sources for Fanx and Nervinho character art.
// DO NOT replace with SVG, emoji, or AI-generated alternatives.
const FANX_ICON = '/assets/images/Fanx_icon-1778869651507.png';
const NERVINHO_ICON = '/assets/images/Nervinho_icon-1778868339003.png';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export type FanxExpression =
  | 'nervous-smile' | 'fake-confidence' | 'screaming' | 'celebrating-too-early' | 'shocked' | 'emotionally-damaged' | 'exhausted' | 'smug-optimism' | 'panic-sweating' | 'delusional-confidence';

export type NervinhoExpression =
  | 'trembling' | 'hiding' | 'panic-attack' | 'stress-eating' | 'crying-dramatically' | 'peeking' | 'collapsing' | 'overwhelmed' | 'suspicious' | 'terrified';

interface CharacterProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  animate?: boolean;
}

// ─── CORE CHARACTER ICON ──────────────────────────────────────────────────────
// Renders a circular icon from the official uploaded character PNG assets.

interface CharacterIconProps {
  src: string;
  size: number;
  alt: string;
  borderColor: string;
  bgColor: string;
  shadowColor: string;
  className?: string;
  style?: React.CSSProperties;
  animate?: boolean;
  animClass?: string;
}

function CharacterIcon({
  src, size, alt,
  borderColor, bgColor, shadowColor,
  className = '', style, animate, animClass = 'character-float',
}: CharacterIconProps) {
  return (
    <div
      className={`relative shrink-0 overflow-hidden ${animate ? animClass : ''} ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: bgColor,
        border: `${Math.max(1.5, size * 0.05)}px solid ${borderColor}`,
        boxShadow: `0 2px ${Math.round(size * 0.18)}px ${shadowColor}`,
        ...style,
      }}
      aria-label={alt}
    >
      <Image
        src={src}
        alt={alt}
        fill
        sizes={`${size}px`}
        style={{
          objectFit: 'contain',
          objectPosition: 'center center',
        }}
        unoptimized
      />
    </div>
  );
}

// ─── FANX INDIVIDUAL EXPRESSION COMPONENTS ───────────────────────────────────
// All use the official Fanx icon PNG — border/shadow color conveys expression mood.

export function FanxNervousSmile({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx nervous smile" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.3)" className={className} style={style} animate={animate} />;
}

export function FanxFakeConfidence({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx fake confidence" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.3)" className={className} style={style} animate={animate} />;
}

export function FanxScreaming({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx screaming" borderColor="#E53E3E" bgColor="#0f0f0f" shadowColor="rgba(229,62,62,0.3)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

export function FanxCelebratingTooEarly({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx celebrating too early" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.4)" className={className} style={style} animate={animate} animClass="character-bounce" />;
}

export function FanxShocked({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx shocked" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.3)" className={className} style={style} animate={animate} />;
}

export function FanxEmotionallyDamaged({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx emotionally damaged" borderColor="#9E9589" bgColor="#1a1a1a" shadowColor="rgba(158,149,137,0.3)" className={className} style={style} animate={animate} />;
}

export function FanxExhausted({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx exhausted" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.2)" className={className} style={style} animate={animate} />;
}

export function FanxSmugOptimism({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx smug optimism" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.3)" className={className} style={style} animate={animate} />;
}

export function FanxPanicSweating({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx panic sweating" borderColor="#4FC3F7" bgColor="#0f0f0f" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

export function FanxDelusionalConfidence({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={FANX_ICON} size={size} alt="Fanx delusional confidence" borderColor="#F5C800" bgColor="#0f0f0f" shadowColor="rgba(245,200,0,0.3)" className={className} style={style} animate={animate} />;
}

// ─── NERVINHO INDIVIDUAL EXPRESSION COMPONENTS ───────────────────────────────
// All use the official Nervinho icon PNG — border/shadow color conveys expression mood.

export function NervinhoTrembling({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho trembling" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

export function NervinhoHiding({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho hiding" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} />;
}

export function NervinhoPanicAttack({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho panic attack" borderColor="#E53E3E" bgColor="#1a1a2e" shadowColor="rgba(229,62,62,0.3)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

export function NervinhoStressEating({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho stress eating" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} />;
}

export function NervinhoCryingDramatically({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho crying dramatically" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.4)" className={className} style={style} animate={animate} />;
}

export function NervinhoPeeking({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho peeking" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} />;
}

export function NervinhoCollapsing({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho collapsing" borderColor="#E53E3E" bgColor="#1a1a2e" shadowColor="rgba(229,62,62,0.3)" className={className} style={style} animate={animate} />;
}

export function NervinhoOverwhelmed({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho overwhelmed" borderColor="#E53E3E" bgColor="#1a1a2e" shadowColor="rgba(229,62,62,0.3)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

export function NervinhoSuspicious({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho suspicious" borderColor="#4FC3F7" bgColor="#1a1a2e" shadowColor="rgba(79,195,247,0.3)" className={className} style={style} animate={animate} />;
}

export function NervinhoTerrified({ size = 40, className = '', style, animate }: CharacterProps) {
  return <CharacterIcon src={NERVINHO_ICON} size={size} alt="Nervinho terrified" borderColor="#E53E3E" bgColor="#1a1a2e" shadowColor="rgba(229,62,62,0.4)" className={className} style={style} animate={animate} animClass="character-shake" />;
}

// ─── CONVENIENCE WRAPPERS ─────────────────────────────────────────────────────

interface FanxProps extends CharacterProps {
  expression?: FanxExpression;
}

interface NervinhoProps extends CharacterProps {
  expression?: NervinhoExpression;
}

export function Fanx({ expression = 'nervous-smile', ...props }: FanxProps) {
  const size = props.size ?? 40;
  const borderMap: Record<string, string> = {
    'screaming': '#E53E3E',
    'emotionally-damaged': '#9E9589',
    'panic-sweating': '#4FC3F7',
  };
  const shadowMap: Record<string, string> = {
    'screaming': 'rgba(229,62,62,0.3)',
    'emotionally-damaged': 'rgba(158,149,137,0.3)',
    'panic-sweating': 'rgba(79,195,247,0.3)',
  };
  const animMap: Record<string, string> = {
    'screaming': 'character-shake',
    'panic-sweating': 'character-shake',
    'celebrating-too-early': 'character-bounce',
  };
  return (
    <CharacterIcon
      src={FANX_ICON}
      size={size}
      alt={`Fanx — ${expression}`}
      borderColor={borderMap[expression] ?? '#F5C800'}
      bgColor="#0f0f0f"
      shadowColor={shadowMap[expression] ?? 'rgba(245,200,0,0.3)'}
      className={props.className}
      style={props.style}
      animate={props.animate}
      animClass={animMap[expression] ?? 'character-float'}
    />
  );
}

export function Nervinho({ expression = 'trembling', ...props }: NervinhoProps) {
  const size = props.size ?? 40;
  const borderMap: Record<string, string> = {
    'panic-attack': '#E53E3E',
    'collapsing': '#E53E3E',
    'overwhelmed': '#E53E3E',
    'terrified': '#E53E3E',
  };
  const shadowMap: Record<string, string> = {
    'panic-attack': 'rgba(229,62,62,0.3)',
    'collapsing': 'rgba(229,62,62,0.3)',
    'overwhelmed': 'rgba(229,62,62,0.3)',
    'terrified': 'rgba(229,62,62,0.4)',
  };
  const animMap: Record<string, string> = {
    'trembling': 'character-shake',
    'panic-attack': 'character-shake',
    'overwhelmed': 'character-shake',
    'terrified': 'character-shake',
  };
  return (
    <CharacterIcon
      src={NERVINHO_ICON}
      size={size}
      alt={`Nervinho — ${expression}`}
      borderColor={borderMap[expression] ?? '#4FC3F7'}
      bgColor="#1a1a2e"
      shadowColor={shadowMap[expression] ?? 'rgba(79,195,247,0.3)'}
      className={props.className}
      style={props.style}
      animate={props.animate}
      animClass={animMap[expression] ?? 'character-float'}
    />
  );
}

// ─── DYNAMIC EXPRESSION SELECTOR ─────────────────────────────────────────────

export function getCharacterForPanicLevel(panicLevel: number): { character: 'fanx' | 'nervinho'; expression: FanxExpression | NervinhoExpression } {
  if (panicLevel >= 9) return { character: 'nervinho', expression: 'panic-attack' };
  if (panicLevel >= 7) return { character: 'nervinho', expression: 'trembling' };
  if (panicLevel >= 5) return { character: 'fanx', expression: 'nervous-smile' };
  if (panicLevel >= 3) return { character: 'fanx', expression: 'fake-confidence' };
  return { character: 'fanx', expression: 'exhausted' };
}

export function CharacterForPanic({ panicLevel, size = 40, animate = true }: { panicLevel: number; size?: number; animate?: boolean }) {
  const { character, expression } = getCharacterForPanicLevel(panicLevel);
  if (character === 'nervinho') {
    return <Nervinho expression={expression as NervinhoExpression} size={size} animate={animate} />;
  }
  return <Fanx expression={expression as FanxExpression} size={size} animate={animate} />;
}

// ─── INLINE MINI ICON (for badges, labels, small slots) ──────────────────────

export function FanxMini({ expression = 'nervous-smile', size = 20 }: { expression?: FanxExpression; size?: number }) {
  return <Fanx expression={expression} size={size} />;
}

export function NervinhoMini({ expression = 'trembling', size = 20 }: { expression?: NervinhoExpression; size?: number }) {
  return <Nervinho expression={expression} size={size} />;
}

// ─── AVATAR WRAPPERS ──────────────────────────────────────────────────────────

interface StickerImageProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
}

/**
 * Fanx character avatar — circular container using official uploaded Fanx icon.
 */
export function FanxImage({ size = 56, className = '', style, alt = 'Fanx — the emotionally unstable football fan' }: StickerImageProps) {
  return (
    <Fanx
      expression="nervous-smile"
      size={size}
      className={className}
      style={style}
    />
  );
}

/**
 * Nervinho character avatar — circular container using official uploaded Nervinho icon.
 */
export function NervinhoImage({ size = 56, className = '', style, alt = 'Nervinho — the anxious chihuahua mascot' }: StickerImageProps) {
  return (
    <Nervinho
      expression="trembling"
      size={size}
      className={className}
      style={style}
    />
  );
}

/**
 * Side-by-side Fanx + Nervinho duo banner using official uploaded assets.
 */
export function StickerBanner({ width = 280, className = '', style, alt = 'Fanx and Nervinho emotional reactions' }: { width?: number; className?: string; style?: React.CSSProperties; alt?: string }) {
  const avatarSize = Math.round(Math.min(width * 0.38, 80));
  return (
    <div
      className={`flex items-center justify-center gap-2 ${className}`}
      style={{ width, ...style }}
      aria-label={alt}
    >
      <Fanx expression="panic-sweating" size={avatarSize} animate />
      <Nervinho expression="panic-attack" size={avatarSize} animate />
    </div>
  );
}

/**
 * Sticker badge — small circular mascot icon using official uploaded asset.
 */
export function StickerBadge({ size = 32, className = '', style, variant = 'fanx' }: { size?: number; className?: string; style?: React.CSSProperties; variant?: 'fanx' | 'nervinho' | 'duo' }) {
  if (variant === 'nervinho') {
    return <NervinhoImage size={size} className={className} style={style} />;
  }
  if (variant === 'duo') {
    return (
      <div className={`flex items-center ${className}`} style={style}>
        <FanxImage size={size} style={{ zIndex: 2 }} />
        <NervinhoImage size={size} style={{ marginLeft: -(size * 0.3), zIndex: 1 }} />
      </div>
    );
  }
  return <FanxImage size={size} className={className} style={style} />;
}

/**
 * @deprecated Use StickerBadge, FanxImage, NervinhoImage, or StickerBanner instead.
 */
export function StickerSheet({ size = 120, className = '', style, alt = 'Fanx and Nervinho sticker collection' }: StickerImageProps) {
  return <StickerBanner width={size} className={className} style={style} alt={alt} />;
}
